import {
  Button,
  Listbox,
  ListboxItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@heroui/react"
import { Trans, useLingui } from "@lingui/react/macro"
import { InfoIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import { ICON_SIZE } from "../utils"

const MAIN = "HAR Viewer"

interface Licenses {
  map: [string, number][]
  texts: string[]
}

function AboutDialog({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}) {
  const { t } = useLingui()
  const [license, setLicense] = useState<[key: string, value: string][]>()
  const [selected, setSelected] = useState<string>()

  useEffect(() => {
    if (license || !isOpen) return

    void import("../licenses.json").then(({ default: object }) => {
      const json = object as unknown as Licenses
      json.map.unshift([MAIN, 0])
      json.texts[0] = "© 2025. " + t`All Rights Reserved.`
      setLicense(json.map.map(([key, value]) => [key, json.texts[value]]))
      setSelected(json.map[0][0])
    })
  }, [license, isOpen, t])

  const keys = useMemo(() => [...(license?.map(([key]) => ({ key })) ?? [])], [license])
  const text = useMemo(
    () => license?.find(([key]) => key === selected)?.[1],
    [license, selected]
  )

  return (
    <Modal isOpen={isOpen} size="5xl" onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader css="flex items-center gap-2">About</ModalHeader>
        <ModalBody>
          <div css="flex flex-row gap-2">
            <Listbox
              disallowEmptySelection
              aria-label="Single selection example"
              css="max-h-[37rem] w-64 overflow-scroll"
              items={keys}
              selectedKeys={selected ? [selected] : undefined}
              selectionMode="single"
              variant="flat"
              onSelectionChange={key => setSelected((key as any).currentKey as string)}
            >
              {({ key }) => <ListboxItem key={key}>{key}</ListboxItem>}
            </Listbox>

            {selected ? (
              <div css="w-full max-w-2xl text-sm">
                <div css="mb-2 w-full border-b border-b-default-200 pb-1">
                  {selected === MAIN ? (
                    <Trans>
                      <strong css="font-semibold">{selected}</strong> is offered under the
                      following terms:
                    </Trans>
                  ) : (
                    <Trans>
                      <strong css="font-semibold">{selected}</strong> is used under the
                      following license:
                    </Trans>
                  )}
                </div>
                <div css="h-[41em] overflow-scroll whitespace-pre-wrap break-words">
                  {text}
                </div>
              </div>
            ) : (
              <div>
                <Trans>Select a license to view its license.</Trans>
              </div>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export function AboutSwitch() {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useLingui()

  return (
    <>
      <Button
        disableRipple
        isIconOnly
        title={t`About`}
        variant="light"
        onPress={() => setIsOpen(true)}
      >
        <InfoIcon size={ICON_SIZE} />
      </Button>
      <AboutDialog isOpen={isOpen} onOpenChange={setIsOpen} />
    </>
  )
}
